import { AppModal, Button } from "@/components/ui";
import { cn } from "@/lib/utils/cn";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { useState } from "react";
import type { SurveyDraftSection } from "../../../../shared/survey-questions";

function SortableSectionRow({ section }: { section: SurveyDraftSection }) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: section.id });
  const title = section.title.trim() || "Untitled section";

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "border-border flex items-center gap-3 border-b px-3 py-3 last:border-b-0",
        isDragging && "bg-background relative z-10 shadow-sm",
      )}
    >
      <button
        type="button"
        className="text-muted-foreground bg-background hover:bg-muted flex size-8 shrink-0 cursor-grab items-center justify-center rounded-md border active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label={`Reorder ${title}`}
      >
        <GripVertical className="size-4" aria-hidden />
      </button>
      <span className="min-w-0 truncate text-sm font-medium">{title}</span>
    </div>
  );
}

export function ReorderSurveySectionsModal({
  sections,
  onClose,
  onSave,
  onRestoreFocus,
}: {
  sections: SurveyDraftSection[];
  onClose: () => void;
  onSave: (sections: SurveyDraftSection[]) => void;
  onRestoreFocus: () => void;
}) {
  const [draft, setDraft] = useState(() =>
    [...sections].sort((a, b) => a.position - b.position),
  );
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    setDraft((current) => {
      const from = current.findIndex((section) => section.id === active.id);
      const to = current.findIndex((section) => section.id === over.id);
      return from < 0 || to < 0 ? current : arrayMove(current, from, to);
    });
  };

  return (
    <AppModal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        onRestoreFocus();
      }}
      title="Reorder sections"
      description="Reorder sections. Questions stay with their section."
      size="lg"
      bodyContained
      overlayClassName="z-[108]"
      className="z-[109]"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              onSave(
                draft.map((section, position) => ({ ...section, position })),
              );
              onClose();
            }}
          >
            Save order
          </Button>
        </div>
      }
    >
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
        <p className="text-muted-foreground text-sm">
          Drag sections into the desired order.
        </p>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={draft.map((section) => section.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="border-border overflow-hidden rounded-xl border">
              {draft.map((section) => (
                <SortableSectionRow key={section.id} section={section} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </AppModal>
  );
}
